import React, { useEffect, useState } from "react";
import { Input, toast, Text } from "design-system";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "selectors/usersSelectors";
import { forgotPasswordSubmitHandler } from "pages/UserAuth/helpers";
import {
  FORGOT_PASSWORD_SUCCESS_TEXT,
  USER_DISPLAY_NAME_CHAR_CHECK_FAILED,
  USER_DISPLAY_NAME_PLACEHOLDER,
  USER_DISPLAY_PICTURE_PLACEHOLDER,
  USER_EMAIL_PLACEHOLDER,
  USER_RESET_PASSWORD,
  USER_DISPLAY_NICKNAME_PLACEHOLDER,
  FORM_VALIDATION_EMPTY_NAME,
} from "ee/constants/messages";
import { logoutUser, updateUserDetails } from "actions/userActions";
import UserProfileImagePicker from "./UserProfileImagePicker";
import {
  GeneralWrapper,
  GeneralFieldWrapper,
  LabelWrapper,
  GeneralFieldItemWrapper,
} from "./StyledComponents";
import { ANONYMOUS_USERNAME } from "constants/userConstants";
import { ALL_LANGUAGE_CHARACTERS_REGEX } from "constants/Regex";
import { createMessage } from "design-system-old/build/constants/messages";
import { notEmptyValidator } from "design-system-old";
import {
  getIsFormLoginEnabled,
  getThirdPartyAuths,
} from "ee/selectors/tenantSelectors";
import { ThirdPartyLogin } from "ee/utils";
import type { ThirdPartyCardProps } from "./ThirdPartyCard";
import { ThirdPartyCard, TitleWrapper } from "./ThirdPartyCard";

import WeChat from "assets/images/WeChat.svg";
import BusinessWeChat from "assets/images/BusinessWeChat.svg";
import Dingding from "assets/images/Dingding.svg";
import {
  WechatOAuthURL,
  BussinessWechatOAuthURL,
  DingdingOAuthURL,
} from "ee/constants/ApiConstants";
import { message, Popconfirm, Button } from "antd";
import type { PopconfirmProps } from "antd";

import { FormOutlined } from "@ant-design/icons";

const nickNameValidator = (
  value: string,
): {
  isValid: boolean;
  message: string;
} => {
  const notEmpty = notEmptyValidator(value);
  if (!notEmpty.isValid) {
    return notEmpty;
  }
  if (!new RegExp(`^[${ALL_LANGUAGE_CHARACTERS_REGEX} 0-9.'-]+$`).test(value)) {
    return {
      isValid: false,
      message: createMessage(USER_DISPLAY_NAME_CHAR_CHECK_FAILED),
    };
  }
  return {
    isValid: true,
    message: "",
  };
};

const nameValidator = (
  value: string,
): {
  isValid: boolean;
  message: string;
} => {
  const notEmpty = notEmptyValidator(value);
  if (!notEmpty.isValid) {
    return notEmpty;
  }
  if (!/^[a-zA-Z][a-zA-Z0-9_-]{3,99}$/.test(value)) {
    return {
      isValid: false,
      message: createMessage(FORM_VALIDATION_EMPTY_NAME),
    };
  }
  return {
    isValid: true,
    message: "",
  };
};

function General() {
  const user = useSelector(getCurrentUser);
  const isFormLoginEnabled = useSelector(getIsFormLoginEnabled);
  const [name, setName] = useState(user?.name || "");
  const [nickName, setNickName] = useState(user?.nickName || "");
  const [isNameEditing, setIsNameEditing] = useState(true);
  const dispatch = useDispatch();
  const thirdPartyAuths = useSelector(getThirdPartyAuths);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const queryParams = new URLSearchParams(window.location.search);
  let showError = false;

  // 初始无name
  useEffect(() => {
    if (name) {
      setIsNameEditing(false);
    }
  }, []);

  const forgotPassword = async () => {
    try {
      await forgotPasswordSubmitHandler({ email: user?.email }, dispatch);
      toast.show(createMessage(FORGOT_PASSWORD_SUCCESS_TEXT, user?.email), {
        kind: "success",
      });
      dispatch(logoutUser());
    } catch (error) {
      toast.show((error as { _error: string })._error, {
        kind: "success",
      });
    }
  };

  const saveName = async () => {
    try {
      if (name && nameValidator(name).isValid) {
        setErrorMessage("");
        await dispatch(
          updateUserDetails({
            name,
          }),
        );
        setIsNameEditing(false);
      } else {
        setErrorMessage(nameValidator(name).message);
      }
    } catch (error) {
      console.error("更新用户详情时出错:", error);
    }
  };

  const saveNickName = () => {
    nickName &&
      nickNameValidator(nickName).isValid &&
      dispatch(
        updateUserDetails({
          nickName,
        }),
      );
  };

  if (user?.email === ANONYMOUS_USERNAME) return <></>;

  const ThirdPartyConfig: ThirdPartyCardProps[] = [
    {
      type: "wechat",
      logo: WeChat,
      handleClick: () => ThirdPartyLogin(WechatOAuthURL),
      title: "微信",
      text: "可以微信登录Pageplug",
      isConneted:
        user?.authorizations?.some((item) => item.source === "WECHAT") ?? false,
    },
    {
      type: "wecom",
      logo: BusinessWeChat,
      handleClick: () => ThirdPartyLogin(BussinessWechatOAuthURL),
      title: "企业微信",
      text: "可以企业微信登录Pageplug",
      isConneted:
        user?.authorizations?.some((item) => item.source === "WECOM") ?? false,
    },
    {
      type: "dingtalk",
      logo: Dingding,
      handleClick: () =>
        ThirdPartyLogin(DingdingOAuthURL, undefined, { baseURL: "/" }),
      title: "钉钉",
      text: "可以钉钉登录Pageplug",
      isConneted:
        user?.authorizations?.some((item) => item.source === "DINGTALK") ??
        false,
    },
  ];

  const confirm: PopconfirmProps["onConfirm"] = (e) => {
    saveName();
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
  };

  return (
    <GeneralWrapper>
      <GeneralFieldWrapper>
        <GeneralFieldItemWrapper>
          <LabelWrapper>
            <Text kind="body-m">
              {createMessage(USER_DISPLAY_PICTURE_PLACEHOLDER)}
            </Text>
          </LabelWrapper>
          <div className="user-profile-image-picker">
            <UserProfileImagePicker />
          </div>
        </GeneralFieldItemWrapper>
      </GeneralFieldWrapper>
      <GeneralFieldWrapper>
        <GeneralFieldItemWrapper>
          <Input
            data-testid="t--display-nickname"
            defaultValue={nickName}
            label={createMessage(USER_DISPLAY_NICKNAME_PLACEHOLDER)}
            labelPosition="top"
            onBlur={saveNickName}
            onChange={setNickName}
            onKeyPress={(ev: React.KeyboardEvent) => {
              if (ev.key === "Enter") {
                saveNickName();
              }
            }}
            placeholder={createMessage(USER_DISPLAY_NICKNAME_PLACEHOLDER)}
            renderAs="input"
            size="md"
            type="text"
          />
        </GeneralFieldItemWrapper>
      </GeneralFieldWrapper>
      <GeneralFieldWrapper>
        <div className="flex items-center">
          <GeneralFieldItemWrapper>
            <Input
              data-testid="t--display-name"
              defaultValue={name}
              label={createMessage(USER_DISPLAY_NAME_PLACEHOLDER)}
              labelPosition="top"
              // onBlur={saveName}
              onChange={setName}
              // onKeyPress={(ev: React.KeyboardEvent) => {
              //   if (ev.key === "Enter") {
              //     saveName();
              //   }
              // }}
              isDisabled={!!name && !isNameEditing}
              placeholder={createMessage(USER_DISPLAY_NAME_PLACEHOLDER)}
              renderAs="input"
              size="md"
              type="text"
            />
          </GeneralFieldItemWrapper>
          {isNameEditing ? (
            name ? (
              <div className="ml-[12px] mt-[27px]">
                <Popconfirm
                  title=""
                  description="请确认用户名信息，确认后将无法修改！"
                  onConfirm={confirm}
                  onCancel={cancel}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button type="primary">更新</Button>
                </Popconfirm>
              </div>
            ) : (
              <FormOutlined
                style={{ marginLeft: 10, marginTop: 25, fontSize: 20 }}
              />
            )
          ) : null}
        </div>
        {errorMessage && (
          <div className="text-red-500 w-[500px]">{errorMessage}</div>
        )}{" "}
      </GeneralFieldWrapper>
      <GeneralFieldWrapper>
        <GeneralFieldItemWrapper>
          <Input
            data-testid="t--user-name"
            defaultValue={user?.email}
            isDisabled
            isReadOnly
            label={createMessage(USER_EMAIL_PLACEHOLDER)}
            labelPosition="top"
            placeholder={createMessage(USER_EMAIL_PLACEHOLDER)}
            renderAs="input"
            size="md"
            type="text"
          />
        </GeneralFieldItemWrapper>
      </GeneralFieldWrapper>

      {/* <FieldWrapper>
        <div
          style={{
            display: "flex",
            flex: "1 1 0%",
            justifyContent: "flex-end",
          }}
        >
          {isFormLoginEnabled && (
            <Button
              kind="secondary"
              onClick={forgotPassword}
              renderAs="a"
              size="md"
            >
              {createMessage(USER_RESET_PASSWORD)}
            </Button>
          )}
        </div>
      </FieldWrapper> */}
      <GeneralFieldWrapper>
        <GeneralFieldItemWrapper>
          <TitleWrapper>第三方登录</TitleWrapper>
          {ThirdPartyConfig.filter((item) =>
            thirdPartyAuths?.includes(item.type),
          )?.map((item) => {
            const { handleClick, isConneted, logo, text, title } = item;
            return (
              <ThirdPartyCard
                handleClick={handleClick}
                isConneted={isConneted}
                key={item.title}
                logo={logo}
                text={text}
                title={title}
              />
            );
          })}
        </GeneralFieldItemWrapper>
      </GeneralFieldWrapper>
    </GeneralWrapper>
  );
}

export default General;
